import React, { createContext, useState, useCallback, ReactNode } from "react";
import api from "../../api/api";
import { ApiResponse, PaginationInfo } from "../../types/api";
import { Invoice, ClientPayment } from "../../types/models";
import { InvoiceContextType } from "../../types/context";

export const InvoiceContext = createContext<InvoiceContextType | undefined>(undefined);

interface InvoiceProviderProps {
  children: ReactNode;
}

export const InvoiceProvider: React.FC<InvoiceProviderProps> = ({ children }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInvoices = useCallback(async (
    filters?: { project_id?: number; status?: string; page?: number; per_page?: number }
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      let url = '/invoices';
      const params = new URLSearchParams();
      if (filters?.project_id) params.append('project_id', filters.project_id.toString());
      if (filters?.status) params.append('status', filters.status);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.per_page) params.append('per_page', filters.per_page.toString());
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let invoicesData: Invoice[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          // Nested structure: response.data.data.data
          invoicesData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Direct array: response.data.data
          invoicesData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          invoicesData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        invoicesData = responseData.data;
      } else if (Array.isArray(responseData)) {
        invoicesData = responseData;
      }
      
      setInvoices(invoicesData as Invoice[]);
      setPagination(paginationData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
      console.error("Failed to fetch invoices:", err);
      setInvoices([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const createInvoice = useCallback(async (invoiceData: any): Promise<Invoice> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<ApiResponse<Invoice>>('/invoices', invoiceData);
      const newInvoice = response.data.data;
      setInvoices((prev) => [...prev, newInvoice]);
      return newInvoice;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateInvoice = useCallback(async (id: number, invoiceData: any): Promise<Invoice> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put<ApiResponse<Invoice>>(`/invoices/${id}`, invoiceData);
      const updatedInvoice = response.data.data;
      setInvoices((prev) =>
        prev.map((invoice) => (invoice.id === id ? updatedInvoice : invoice))
      );
      return updatedInvoice;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteInvoice = useCallback(async (id: number): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/invoices/${id}`);
      setInvoices((prev) => prev.filter((invoice) => invoice.id !== id));
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getInvoice = useCallback(async (id: number): Promise<Invoice> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<ApiResponse<Invoice>>(`/invoices/${id}`);
      return response.data.data;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getInvoicePayments = useCallback(async (invoiceId: number): Promise<ClientPayment[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<ApiResponse<ClientPayment[]>>(`/invoices/${invoiceId}/payments`);
      return response.data.data || [];
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const contextValue: InvoiceContextType = {
    invoices,
    pagination,
    loading,
    error,
    fetchInvoices,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    getInvoice,
    getInvoicePayments,
  };

  return (
    <InvoiceContext.Provider value={contextValue}>
      {children}
    </InvoiceContext.Provider>
  );
};

export default InvoiceProvider;

